import React, { useEffect, useState } from "react"
import { Box, CircularProgress } from "@mui/material"
import { AppDialog, AppDialogBody, AppDialogHeader, AppNote } from "@components"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"
import { AutocompleteSeeker } from "@ui-seeker/core"
import { getSubmit } from "@ui-seeker/utils"
import { BACKEND_APIS } from "@constants"

const AUTOEUROPE_API = BACKEND_APIS.AUTOEUROPE_PRICING_TOOL

export default function SimulateModal({ open, onClose, pricingToolId }) {
	const [reportOptions, setReportOptions] = useState(null)
	const [executionOptions, setExecutionOptions] = useState(null)

	const [selectedReport, setSelectedReport] = useState(null)
	const [selectedExecution, setSelectedExecution] = useState(null)
	const [selectedOffice, setSelectedOffice] = useState(null)

	const [isChecking, setIsChecking] = useState(false)
	const [noInfo, setNoInfo] = useState(false)
	const [success, setSuccess] = useState(false)

	// Each execution carries its own `offices`; derive the office options from the selected one.
	const officeOptions = executionOptions?.find(e => e.value === selectedExecution)?.offices ?? []

	useEffect(() => {
		if (!open) return
		getSubmit(`${AUTOEUROPE_API}/reports`, { idPricingTool: pricingToolId })
			.then(data => setReportOptions(data ?? []))
	}, [open, pricingToolId])

	useEffect(() => {
		if (!selectedReport) { setExecutionOptions(null); setSelectedExecution(null); setSelectedOffice(null); return }
		setExecutionOptions(null)
		setSelectedExecution(null)
		setSelectedOffice(null)
		getSubmit(`${AUTOEUROPE_API}/executions`, { idReport: selectedReport })
			.then(data => setExecutionOptions(data ?? []))
	}, [selectedReport])

	const handleCheck = async () => {
		setNoInfo(false)
		setSuccess(false)
		setIsChecking(true)
		try {
			const result = await getSubmit(`${BACKEND_APIS.INTERMEDIARY}/pricing-tool/autoeurope/check`, {
				idReportExecution: selectedExecution,
				idOfficePickup: selectedOffice,
				idPricingTool: pricingToolId,
			})
			if (result?.url) {
				window.open(result.url, '_blank')
				setSuccess(true)
			} else {
				setNoInfo(true)
			}
		} finally {
			setIsChecking(false)
		}
	}

	const handleClose = () => {
		setReportOptions(null)
		setExecutionOptions(null)
		setSelectedReport(null)
		setSelectedExecution(null)
		setSelectedOffice(null)
		setIsChecking(false)
		setNoInfo(false)
		setSuccess(false)
		onClose()
	}

	const canCheck = selectedReport && selectedExecution && selectedOffice

	return (
		<AppDialog open={open} onClose={handleClose} maxWidth="sm">
			<AppDialogHeader icon="play" title="Simulate Pricing Tool" onClose={handleClose}/>
			<AppDialogBody>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					{reportOptions === null
						? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24}/></Box>
						: <>
							<AutocompleteSeeker
								label="Report"
								placeholder="Select a report"
								options={reportOptions}
								value={selectedReport}
								onChange={e => { setSelectedReport(e.target.value); setNoInfo(false); setSuccess(false) }}
							/>
							{selectedReport && (
								executionOptions === null
									? <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={24}/></Box>
									: <AutocompleteSeeker
										label="Execution"
										placeholder="Select an execution"
										options={executionOptions}
										value={selectedExecution}
										onChange={e => { setSelectedExecution(e.target.value); setSelectedOffice(null); setNoInfo(false); setSuccess(false) }}
									/>
							)}
							{selectedExecution && (
								<AutocompleteSeeker
									label="Office"
									placeholder="Select an office"
									options={officeOptions}
									value={selectedOffice}
									onChange={e => { setSelectedOffice(e.target.value); setNoInfo(false); setSuccess(false) }}
								/>
							)}
							{canCheck && (
								<Box>
									<ButtonLoadingSeeker
										isLoading={isChecking}
										startIcon={<IconSeeker icon="play"/>}
										onClick={handleCheck}
										fullWidth
									>
										Check
									</ButtonLoadingSeeker>
								</Box>
							)}
							{success && (
								<AppNote severity="success">Simulación generada correctamente. Se ha abierto en una nueva pestaña.</AppNote>
							)}
							{noInfo && (
								<AppNote severity="info">No hay información disponible</AppNote>
							)}
						</>
					}
				</Box>
			</AppDialogBody>
		</AppDialog>
	)
}